import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

function StudentTransportationReport() {
  const storedSession = localStorage.getItem("currentSessionId") || "";
  const [session] = useState(storedSession);

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedMode, setSelectedMode] = useState("");
  const [modes, setModes] = useState([]);

  const printRef = useRef();

  /* ================= FETCH CLASSES ================= */
  useEffect(() => {
    if (!session) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${session}`)
      .then((res) => setClasses(res.data))
      .catch((err) => console.error(err));
  }, [session]);

  /* ================= FETCH ALL STUDENTS ================= */
  useEffect(() => {
    if (!session) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: { sessionId: session },
      })
      .then((res) => {
        setStudents(res.data);
        setFilteredStudents(res.data);
        setAllStudents(res.data);

        // Extract unique modes of transportation
        const uniqueModes = [
          ...new Set(res.data.map((student) => student.transportationMode)),
        ];
        setModes(uniqueModes);
      })
      .catch((err) => console.error(err));
  }, [session]);

  /* ================= FETCH SECTIONS ================= */
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }

    const className = classes.find((c) => c._id === selectedClass)?.className;
    if (!className) return;

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(
          className
        )}/getSectionsByClassName?sessionId=${session}`
      )
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data.sections || [];
        setSections(data);
      })
      .catch((err) => console.error(err));
  }, [selectedClass, session, classes]);

  /* ================= FILTER STUDENTS ================= */
const filteredStudentsMemo = useMemo(() => {
  return students.filter((student) => {
    // Match class by class name (or whatever field corresponds to student class)
    const className = classes.find((cls) => cls._id === selectedClass)?.className;

    // Class and Section filters
    const matchesClass =
      !selectedClass || student.class === className; // Now match by className, not ObjectId
    const matchesSection =
      !selectedSection || student.section === selectedSection;

    // Mode of Transportation filter
    const matchesMode = !selectedMode || student.transportationMode === selectedMode;

    return matchesClass && matchesSection && matchesMode;
  });
}, [students, selectedClass, selectedSection, selectedMode, classes]);

  // Update filtered students when memoized filtered data changes
  useEffect(() => {
    setFilteredStudents(filteredStudentsMemo);
  }, [filteredStudentsMemo]);

  /* ================= EXPORT ================= */
  const exportToExcel = () => {
    const data = filteredStudents.map((stu, index) => ({
      "Sr No.": index + 1,
      "Admission No": stu.admission_id,
      Name: `${stu.firstName} ${stu.lastName}`,
      Class: stu.class,
      Section: stu.section,
      Gender: stu.gender,
      Mode: stu.transportationMode,
      Category: stu.category,
      Financial: stu.financial,
      Status: stu.status,
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "Student_Transportation_Report.xlsx");
  };

  /* ================= PRINT ================= */
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const newWindow = window.open("", "", "width=900,height=650");
    newWindow.document.write(`
      <html>
        <head>
          <title>Student Report</title>
          <style>
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align:center; }
            th { background: #f3f4f6; }
          </style>
        </head>
        <body>
          <h2 style="text-align:center;">Student Transportation Report</h2>
          ${printContents}
        </body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
  };

  /* ================= BADGES ================= */
  const totalStudents = allStudents.length;
  const active = allStudents.filter(
    (s) => s.status?.toLowerCase() === "active"
  ).length;
  const inactive = totalStudents - active;

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-xl p-8">

        {/* HEADER */}
        <h2 className="text-2xl font-bold mb-6 text-gray-700">
          Student Transportation Report
        </h2>

        {/* SUMMARY */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
            <p className="text-sm text-blue-600">Total Students</p>
            <p className="text-2xl font-bold text-blue-700">{totalStudents}</p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
            <p className="text-sm text-green-600">Active</p>
            <p className="text-2xl font-bold text-green-700">{active}</p>
          </div>
          <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
            <p className="text-sm text-red-600">Inactive</p>
            <p className="text-2xl font-bold text-red-700">{inactive}</p>
          </div>
        </div>

        {/* FILTERS */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {/* Class Filter */}
          <select
            value={selectedClass}
            onChange={(e) => {
              setSelectedClass(e.target.value);
              setSelectedSection(""); // Reset section when class changes
            }}
            className="border rounded px-3 py-2 shadow-sm"
          >
            <option value="">Select Class</option>
            {classes.map((cls) => (
              <option key={cls._id} value={cls._id}>
                {cls.className}
              </option>
            ))}
          </select>

          {/* Section Filter */}
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="border rounded px-3 py-2 shadow-sm"
            disabled={!selectedClass}
          >
            <option value="">Select Section</option>
            {sections.map((sec) => (
              <option key={sec._id} value={sec.sectionName}>
                {sec.sectionName}
              </option>
            ))}
          </select>

          {/* Transportation Mode Filter */}
          <select
            value={selectedMode}
            onChange={(e) => setSelectedMode(e.target.value)}
            className="border rounded px-3 py-2 shadow-sm"
          >
            <option value="">Select Mode of Transport</option>
            {modes.map((mode, index) => (
              <option key={index} value={mode}>
                {mode}
              </option>
            ))}
          </select>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex justify-end gap-4 mb-4">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded shadow hover:bg-green-700"
          >
            Export Excel
          </button>
          <button
            onClick={handlePrint}
            className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
          >
            Print
          </button>
        </div>

        {/* TABLE */}
        <div ref={printRef} className="overflow-x-auto">
          <table className="min-w-full border text-center">
            <thead className="bg-gray-200">
              <tr>
                <th className="border px-2 py-2">Sr</th>
                <th className="border px-2 py-2">Admission</th>
                <th className="border px-2 py-2">Name</th>
                <th className="border px-2 py-2">Class</th>
                <th className="border px-2 py-2">Section</th>
                <th className="border px-2 py-2">Gender</th>
                <th className="border px-2 py-2">Mode</th>
                <th className="border px-2 py-2">Category</th>
                <th className="border px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((s, i) => (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="border px-2 py-2">{i + 1}</td>
                  <td className="border px-2 py-2">{s.admission_id}</td>
                  <td className="border px-2 py-2">
                    {s.firstName} {s.lastName}
                  </td>
                  <td className="border px-2 py-2">{s.class}</td>
                  <td className="border px-2 py-2">{s.section}</td>
                  <td className="border px-2 py-2">{s.gender}</td>
                  <td className="border px-2 py-2">{s.transportationMode}</td>
                  <td className="border px-2 py-2">{s.category}</td>
                  <td className="border px-2 py-2">
                    <span
                      className={`px-2 py-1 text-white rounded ${
                        s.status?.toLowerCase() === "active"
                          ? "bg-green-600"
                          : "bg-red-600"
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}

export default StudentTransportationReport;
